import { isAxiosError } from 'axios';
import { SalesforceSupplierSearchResult, VendorLookupSearchResult } from 'sb/api/vendor';
import { VendorAdmin } from 'sb/models/prisma/VendorAdmin';
import { VendorFormValues } from './vendorsPage.types';

export const SALESFORCE_ID_TAG_COLORS = [
  '#cf1322',
  '#d46b08',
  '#d4b106',
  '#389e0d',
  '#08979c',
  '#1d39c4',
  '#531dab',
  '#c41d7f'
] as const;

export const PANE_HEADER_LOGO_CLASS = 'h-7 w-7 rounded object-contain';
export const PANE_HEADER_FALLBACK_CLASS =
  'flex h-7 w-7 items-center justify-center rounded bg-blue-100 text-xs font-bold text-blue-600';
export const DOMAIN_ONLY_PATTERN = /^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

export const toVendorFormValues = (selectedVendorRecord: VendorAdmin): VendorFormValues => ({
  ...selectedVendorRecord,
  fiscal_year_end: selectedVendorRecord.fiscal_year_end ? selectedVendorRecord.fiscal_year_end.split('-') : undefined
});

export const normalizeWebsiteDomain = (websiteOrDomain?: string | null): string | undefined => {
  if (!websiteOrDomain) {
    return undefined;
  }
  const trimmedValue = websiteOrDomain.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const withoutProtocol = trimmedValue.replace(/^https?:\/\//i, '');
  const hostname = withoutProtocol.split('/')[0]?.split('?')[0]?.split('#')[0]?.split(':')[0]?.trim();
  return hostname || undefined;
};

export const getMigrationErrorStatus = (error: unknown): number | undefined => {
  if (isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
};

export const getMigrationErrorMessage = (error: unknown): string | undefined => {
  if (isAxiosError(error)) {
    const responseMessage = error.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      return responseMessage;
    }
  }
  return undefined;
};

export const buildSharedVisibleSalesforceIds = (
  vendorLookupResults: VendorLookupSearchResult[],
  salesforceSupplierResults: SalesforceSupplierSearchResult[]
): Set<string> => {
  const visibleVendorSalesforceIds = new Set(
    vendorLookupResults
      .map((item) => item.salesforce_id?.trim())
      .filter((salesforceId): salesforceId is string => Boolean(salesforceId))
  );
  return new Set(
    salesforceSupplierResults
      .map((item) => item.Id?.trim())
      .filter((salesforceId): salesforceId is string =>
        Boolean(salesforceId && visibleVendorSalesforceIds.has(salesforceId))
      )
  );
};

export const getSalesforceTagColor = (
  sharedVisibleSalesforceIds: Set<string>,
  salesforceId?: string | null
): string => {
  const trimmedSalesforceId = salesforceId?.trim();
  if (!trimmedSalesforceId || !sharedVisibleSalesforceIds.has(trimmedSalesforceId)) {
    return 'default';
  }

  let hashValue = 0;
  for (let index = 0; index < trimmedSalesforceId.length; index += 1) {
    hashValue = (hashValue * 31 + trimmedSalesforceId.charCodeAt(index)) >>> 0;
  }

  return SALESFORCE_ID_TAG_COLORS[hashValue % SALESFORCE_ID_TAG_COLORS.length];
};
